import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Linkedin, MessageCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";
import ArticleRow from "@/components/ArticleRow";
import NotFound from "@/pages/NotFound";
import { formatDate, readingMinutes } from "@/lib/format";
import { categoryLabel, categoryPage } from "@/lib/categories";

function CategoryTag({ category }) {
  const { lang } = useLang();
  const cls = "border border-gold/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-bright";
  const cp = categoryPage(category);
  if (!cp) {
    return <span className={cls} data-testid="article-category">{categoryLabel(category, lang)}</span>;
  }
  return (
    <Link to={`/categoria/${cp.slug}`} data-testid="article-category" className={`${cls} transition-colors hover:bg-gold hover:text-navy`}>
      {categoryLabel(category, lang)}
    </Link>
  );
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const { lang, tr } = useLang();
  const t = tr("common");
  const [article, setArticle] = useState(undefined);
  const [related, setRelated] = useState([]);

  useSEO("article");

  useEffect(() => {
    setArticle(undefined);
    setRelated([]);
    window.scrollTo(0, 0);
    api
      .get(`/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        api
          .get("/articles", { params: { section: data.section } })
          .then(({ data: list }) => setRelated((list || []).filter((x) => x.slug !== slug).slice(0, 2)))
          .catch(() => {});
      })
      .catch(() => setArticle(null));
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const c = article[lang] || article.it || {};
    const sectionPath = article.section === "knowledge" ? "knowledge-center" : "insights";
    const pageUrl = `${window.location.origin}/${sectionPath}/${article.slug}`;
    document.title = `${c.title} — LB Trade Advisory`;

    const setMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", c.excerpt || "");
    setMeta("property", "og:title", `${c.title} — LB Trade Advisory`);
    setMeta("property", "og:description", c.excerpt || "");
    setMeta("property", "og:type", "article");
    setMeta("property", "og:url", pageUrl);
    setMeta("property", "og:image", `${window.location.origin}/api/og/${article.slug}`);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", `${c.title} — LB Trade Advisory`);
    setMeta("name", "twitter:image", `${window.location.origin}/api/og/${article.slug}`);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", pageUrl);
  }, [article, lang]);

  if (article === null) return <NotFound />;
  if (!article) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-navy">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-ivory/40">{t.loading}</p>
      </div>
    );
  }

  const c = article[lang] || article.it || {};
  const backTo = article.section === "knowledge" ? "/knowledge-center" : "/insights";
  const backLabel = article.section === "knowledge" ? t.knowledgeLabel : t.insightsLabel;
  const paragraphs = (c.body || "").split("\n\n").filter(Boolean);
  const mins = readingMinutes(c.body);

  return (
    <>
      <article data-testid="article-page" className="relative overflow-hidden bg-navy pb-24 noise">
        <div className="pointer-events-none absolute right-0 top-0 h-[460px] w-[460px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="relative mx-auto max-w-4xl px-6 pt-32 lg:px-10 lg:pt-44">
          <Reveal>
            <Link
              to={backTo}
              data-testid="article-back-link"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/50 transition-colors hover:text-gold-bright"
            >
              <ChevronLeft className="h-4 w-4" /> {backLabel}
            </Link>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <CategoryTag category={article.category} />
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ivory/40">
                {formatDate(article.published_at, lang)} · {mins} {t.minRead}
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.14}>
            <h1 className="mt-8 font-serif text-3xl leading-[1.15] tracking-tight text-ivory sm:text-4xl lg:text-5xl" data-testid="article-title">
              {c.title}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 border-l-2 border-gold/60 pl-6 font-serif text-lg italic leading-relaxed text-gold-bright/90 sm:text-xl" data-testid="article-excerpt">
              {c.excerpt}
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="gold-rule mt-12" />
          </Reveal>
          {article.cover_url && (
            <Reveal delay={0.3}>
              <div className="mt-12 overflow-hidden border border-gold/25 frame-clipped">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${article.cover_url}`}
                  alt={c.title}
                  className="aspect-[16/9] w-full object-cover"
                  data-testid="article-cover"
                />
              </div>
            </Reveal>
          )}
          <Reveal delay={0.3}>
            <div className="article-body mt-12 text-base lg:text-lg" data-testid="article-body">
              {paragraphs.map((p, i) => {
                const lines = p.split("\n").filter((l) => l.trim());
                const heading = lines[0] && /^#{2,3}\s+/.test(lines[0].trim())
                  ? lines[0].trim().replace(/^#{2,3}\s+/, "").trim()
                  : null;
                const text = (heading ? lines.slice(1) : lines).join(" ").trim();
                return (
                  <div key={i}>
                    {heading && <h3 data-testid={`article-subheading-${i}`}>{heading}</h3>}
                    {text && <p>{text}</p>}
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.35}>
            <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-gold/15 pt-7" data-testid="article-share">
              <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{t.share}</span>
              {(() => {
                const sectionPath = article.section === "knowledge" ? "knowledge-center" : "insights";
                const u = encodeURIComponent(`${window.location.origin}/${sectionPath}/${article.slug}`);
                const q = encodeURIComponent(c.title || "");
                const cls = "inline-flex items-center gap-2 border border-gold/25 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ivory/70 transition-colors hover:border-gold/60 hover:text-gold-bright";
                return (
                  <>
                    <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${u}`} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin" className={cls}>
                      <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                    </a>
                    <a href={`https://wa.me/?text=${q}%20-%20${u}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className={cls}>
                      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                    </a>
                    <a href={`https://twitter.com/intent/tweet?text=${q}&url=${u}`} target="_blank" rel="noopener noreferrer" data-testid="share-x" className={cls}>
                      <span className="font-serif text-[12px] font-bold">X</span> Post
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard?.writeText(decodeURIComponent(u));
                        toast.success(t.linkCopied);
                      }}
                      data-testid="share-copy"
                      className={cls}
                    >
                      <Link2 className="h-3.5 w-3.5" /> {t.copyLink}
                    </button>
                    <a
                      href="https://www.linkedin.com/company/lb-trade-advisory"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="article-follow-linkedin"
                      className={cls}
                    >
                      <Linkedin className="h-3.5 w-3.5" /> {t.followPage}
                    </a>
                  </>
                );
              })()}
            </div>
          </Reveal>
        </div>
      </article>

      {related.length > 0 && (
        <section data-testid="related-section" className="bg-navy-light px-6 py-16 lg:px-10 lg:py-24">
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <h2 className="font-serif text-2xl tracking-tight text-ivory sm:text-3xl">
                {t.related.split(" ")[0]} <em className="text-gold-bright">{t.related.split(" ").slice(1).join(" ")}</em>
              </h2>
            </Reveal>
            <div className="mt-8">
              {related.map((a, i) => (
                <Reveal key={a.id || a.slug} delay={i * 0.08}>
                  <ArticleRow article={a} index={i} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
